/** The list behind the sheet is another screen's. The sheet finds a row by [data-person-row], which a table may set on each row; failing that, by the row's link to ?person=<id>. */
import { STANDING_LABEL, type StandingValue } from "../kit";

const TABBABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The row's link to ?person=<id>. */
function personLink(id: number): HTMLAnchorElement | null {
  for (const link of document.querySelectorAll<HTMLAnchorElement>('a[href*="person="]')) {
    if (new URL(link.href, location.href).searchParams.get("person") === String(id)) return link;
  }
  return null;
}

export function rowFor(id: number): HTMLElement | null {
  const row = document.querySelector<HTMLElement>(`[data-person-row="${id}"]`);
  if (row) return row;
  const link = personLink(id);
  return link?.closest<HTMLElement>('tr, [role="row"], li') ?? link;
}

/** What takes focus when the sheet leaves: the row's link to the person (what opened it), else the row's first control, else the row itself made focusable. */
export function rowTarget(id: number): HTMLElement | null {
  const row = rowFor(id);
  if (!row) return null;
  const link = personLink(id);
  if (link && row.contains(link)) return link;
  const target = row.matches(TABBABLE) ? row : row.querySelector<HTMLElement>(TABBABLE);
  if (target) return target;
  row.tabIndex = -1;
  return row;
}

/** The row, or, for a person the page does not list (a deep link, a palette hit), the first link under the page's heading: focus stays in the page, never on the body. */
export function restoreTarget(id: number): HTMLElement | null {
  const target = rowTarget(id);
  if (target) return target;
  const heading = document.querySelector("main h1");
  const links = document.querySelectorAll<HTMLElement>("main a[href]");
  return [...links].find((link) => !heading || heading.compareDocumentPosition(link) & Node.DOCUMENT_POSITION_FOLLOWING) ?? null;
}

/** Clear of the top bar above and the page's foot below: a row nearer an edge than this is brought to the middle. */
const EDGE = { top: 96, bottom: 48 };

/** J and K walk a list longer than the screen: keep the row the sheet shows where it can be seen. */
export function revealRow(id: number): void {
  const row = rowFor(id);
  if (!row) return;
  const { top, bottom } = row.getBoundingClientRect();
  if (top < EDGE.top || bottom > window.innerHeight - EDGE.bottom) row.scrollIntoView?.({ block: "center" });
}

/** What the row already on screen says about the person (their name, and their standing when the list has that column), so the sheet's first frame reads before its data lands. */
export function rowSeed(id: number): { name: string; standing: StandingValue | null } | null {
  const row = rowFor(id);
  const name = row?.querySelector("[data-name]")?.textContent?.trim();
  if (!row || !name) return null;
  const standing = row.querySelector<HTMLElement>("[data-standing]")?.dataset.standing;
  return { name, standing: standing && standing in STANDING_LABEL ? (standing as StandingValue) : null };
}
